import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Button, Layout, Menu, Spin, theme, Typography } from 'antd'
import type { MenuProps } from 'antd'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  CommentOutlined,
  UserOutlined,
  TeamOutlined,
  MenuOutlined,
  ControlOutlined,
  BookOutlined,
  RobotOutlined,
  CloudOutlined,
  ToolOutlined,
  ScanOutlined,
  CheckCircleOutlined,
  ApartmentOutlined,
  CloudSyncOutlined,
  TagsOutlined,
  FileSearchOutlined,
  SettingOutlined,
  AuditOutlined,
  CopyOutlined,
  DatabaseOutlined,
  ApiOutlined,
} from '@ant-design/icons'
import { message } from 'antd'
import { adminApi, getAdminToken, setAdminToken } from '../api'
import {
  canAccessManagePath,
  firstManagePath,
  hasAdminMenuAccess,
  type AdminMenuRow,
} from '../lib/adminMenus'
import ChangePasswordModal from '../components/ChangePasswordModal'
import { useMustChangePassword } from '../lib/useMustChangePassword'
import { validateSession } from '../lib/sessionAuth'

const { Header, Sider, Content } = Layout
const { Text } = Typography

const ICONS: Record<string, ReactNode> = {
  system_manage: <SettingOutlined />,
  users: <UserOutlined />,
  org_structure: <ApartmentOutlined />,
  org_sync: <CloudSyncOutlined />,
  roles: <TeamOutlined />,
  menus: <MenuOutlined />,
  infra_storage: <DatabaseOutlined />,
  models: <RobotOutlined />,
  storage: <CloudOutlined />,
  neo4j: <ApartmentOutlined />,
  ocr: <ScanOutlined />,
  sms: <CommentOutlined />,
  mcp: <ToolOutlined />,
  knowledge_process: <ControlOutlined />,
  settings: <ControlOutlined />,
  attr_extract: <TagsOutlined />,
  graph: <ApartmentOutlined />,
  doc_locate: <FileSearchOutlined />,
  cleaning: <AuditOutlined />,
  dedup_settings: <CopyOutlined />,
  search_qa: <CommentOutlined />,
  search_dict: <BookOutlined />,
  qa: <ControlOutlined />,
  'chat-qa': <CheckCircleOutlined />,
  agents: <RobotOutlined />,
  knowledge_open: <DatabaseOutlined />,
  datasets: <DatabaseOutlined />,
  open_api: <ApiOutlined />,
  dataset_sync: <CloudOutlined />,
  open_api_logs: <AuditOutlined />,
  data_process: <ControlOutlined />,
  pipeline_jobs: <AuditOutlined />,
}

function buildAdminMenuItems(menus: AdminMenuRow[]): MenuProps['items'] {
  const sorted = [...menus].sort((a, b) => a.sort_order - b.sort_order)
  const childrenMap = new Map<number, AdminMenuRow[]>()
  for (const m of sorted) {
    if (m.parent_id != null) {
      const list = childrenMap.get(m.parent_id) || []
      list.push(m)
      childrenMap.set(m.parent_id, list)
    }
  }
  const roots = sorted.filter((m) => m.parent_id == null)

  const toItem = (m: AdminMenuRow): NonNullable<MenuProps['items']>[number] => {
    const kids = (childrenMap.get(m.id) || []).sort((a, b) => a.sort_order - b.sort_order)
    if (kids.length > 0) {
      return {
        key: `group-${m.code}`,
        icon: ICONS[m.code] || <MenuOutlined />,
        label: m.name,
        children: kids.map(toItem),
      }
    }
    return {
      key: m.path || `menu-${m.code}`,
      icon: ICONS[m.code] || <MenuOutlined />,
      label: m.name,
    }
  }

  return roots.map(toItem)
}

function resolveActiveMenuPath(menus: AdminMenuRow[], pathname: string): string {
  const leaves = menus.filter((m) => m.path)
  const matches = leaves.filter(
    (m) => pathname === m.path || (pathname.startsWith(`${m.path}/`) && m.path.length > '/manage'.length),
  )
  if (matches.length === 0) return pathname
  return matches.sort((a, b) => b.path.length - a.path.length)[0].path
}

function parentGroupKey(menus: AdminMenuRow[], pathname: string): string | null {
  const activePath = resolveActiveMenuPath(menus, pathname)
  const current = menus.find((m) => m.path === activePath)
  if (!current?.parent_id) return null
  const parent = menus.find((m) => m.id === current.parent_id)
  return parent ? `group-${parent.code}` : null
}

export default function AdminLayout() {
  const { token } = theme.useToken()
  const loc = useLocation()
  const navigate = useNavigate()
  const contentRef = useRef<HTMLDivElement>(null)
  const [menus, setMenus] = useState<AdminMenuRow[]>([])
  const [user, setUser] = useState<{ username: string } | null>(null)
  const [loading, setLoading] = useState(true)
  const [openKeys, setOpenKeys] = useState<string[]>([])
  const {
    mustChangePassword,
    applyMustChangePassword,
    refreshMustChangePassword,
    deferPasswordChange,
    completePasswordChange,
  } = useMustChangePassword()

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      const ok = await validateSession()
      if (cancelled) return
      if (!ok) {
        void navigate(`/login?redirect=${encodeURIComponent(loc.pathname)}`, { replace: true })
        setLoading(false)
        return
      }
      try {
        const r = await adminApi.get<{
          user: { username: string; display_name?: string; must_change_password?: boolean }
          menus: AdminMenuRow[]
        }>('/admin/auth/me')
        if (cancelled) return
        const u = r.data.user
        const nextMenus = r.data.menus
        applyMustChangePassword(!!u.must_change_password)
        if (!hasAdminMenuAccess(nextMenus)) {
          message.warning('当前账号无后台管理权限')
          void navigate('/', { replace: true })
          return
        }
        setUser(u)
        setMenus(nextMenus)
        setAdminToken(getAdminToken(), u.display_name || u.username)
      } catch {
        if (cancelled) return
        setAdminToken(null)
        void navigate(`/login?redirect=${encodeURIComponent(loc.pathname)}`, { replace: true })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [navigate])

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 })
  }, [loc.pathname])

  useEffect(() => {
    const group = parentGroupKey(menus, loc.pathname)
    if (group) {
      setOpenKeys([group])
    }
  }, [loc.pathname, menus])

  useEffect(() => {
    if (loading || !menus.length) return
    if (!canAccessManagePath(menus, loc.pathname)) {
      const fallback = firstManagePath(menus)
      if (fallback) void navigate(fallback, { replace: true })
      else void navigate('/', { replace: true })
    }
  }, [loading, loc.pathname, menus, navigate])

  const menuItems = useMemo(() => buildAdminMenuItems(menus), [menus])
  const selectedMenuPath = useMemo(
    () => resolveActiveMenuPath(menus, loc.pathname),
    [menus, loc.pathname],
  )

  const logout = () => {
    setAdminToken(null)
    void navigate('/login?redirect=/manage/users')
  }

  if (loading) {
    return (
      <div style={{ padding: 80, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!hasAdminMenuAccess(menus)) {
    return null
  }

  return (
    <>
      <ChangePasswordModal
        open={mustChangePassword}
        onDefer={deferPasswordChange}
        onSuccess={() => {
          completePasswordChange()
          void refreshMustChangePassword()
        }}
      />
      <Layout className="admin-shell">
      <Sider width={220} className="admin-sider" theme="dark">
        <div className="admin-sider-brand">
          <Text strong style={{ color: 'inherit' }}>
            后台管理
          </Text>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedMenuPath]}
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys as string[])}
          items={menuItems}
          className="admin-sider-menu"
          onClick={({ key }) => {
            if (typeof key === 'string' && key.startsWith('/manage')) {
              void navigate(key)
            }
          }}
        />
      </Sider>
      <Layout className="admin-main">
        <Header
          className="admin-topbar"
          style={{ background: token.colorBgContainer, borderBottom: `1px solid ${token.colorBorderSecondary}` }}
        >
          <Text>{user?.username}</Text>
          <Button onClick={logout}>退出</Button>
        </Header>
        <Content ref={contentRef} className="admin-content">
          <div
            className="admin-content-inner"
            style={{ background: token.colorBgContainer }}
          >
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
    </>
  )
}
